import type {
  MetadataReducer,
  MetricsMetadataGetter,
} from "./components/AssetsTree/AssetsTree.types";
import type { MetricsTableRow } from "./MetricsTable/MetricsTable.interface";
import { ROW_KEY_URL_DELIMITER } from "./MetricsTabView.constants";
import type { TreeAlerts } from "./NodeTree/components/NodeTreeNode/NodeTreeNode.props";

/**
 * Boundary codec for row uniqueKeys in the URL. Internal format stays
 * `<metric_id>,<asset_value>`; URL format swaps the comma for `~`.
 * Decode is lenient (no-op when `~` is absent) so old `%2C` bookmarks still work.
 */
export const encodeRowKeyForUrl = (key: string) =>
  key.replaceAll(",", ROW_KEY_URL_DELIMITER);
export const decodeRowKeyFromUrl = (raw: string | null): string | null =>
  raw == null ? null : raw.replaceAll(ROW_KEY_URL_DELIMITER, ",");

export const metadataReducer: MetadataReducer<TreeAlerts> = (acc, cur) => ({
  metrics: (cur.metadata?.metrics ?? 0) + (acc?.metrics ?? 0),
  tests: (cur.metadata?.tests ?? 0) + (acc?.tests ?? 0),
  alerts: (cur.metadata?.alerts ?? 0) + (acc?.alerts ?? 0),
  isCritical: !!cur.metadata?.isCritical || !!acc?.isCritical,
});

const getMetricKey = (m: { path: string }) => m.path;

export const getTransformedAssets: MetricsMetadataGetter<TreeAlerts> = (
  metricsData: MetricsTableRow[],
  app_pit?: string
) => {
  const filtered = metricsData.filter((m) => !!m.is_metric_enabled);
  const grouped = Object.groupBy(filtered, (m) => getMetricKey(m));
  return Object.fromEntries(
    Object.entries(grouped)
      .toSorted(([a], [b]) => b.localeCompare(a))
      .map(([path, rows]) => {
        // /api/metrics_conf fans a `cnt_values` metric into one row per asset_value
        // (plus an empty-value row), and every one of those rows carries the *same*
        // `tests` array. Rows without a metric_id are the compound rows getEnhancedMetrics
        // appends for the table's grouping, which duplicate a metric that is already here.
        // Counting rows would report metrics x tests; count each metric once.
        const metrics = [
          ...new Map(
            rows?.filter((m) => m.metric_id != null).map((m) => [m.metric_id, m])
          ).values(),
        ];

        const testsCount = metrics
          .flatMap((m) => m.tests?.length)
          .filter(Number)
          .reduce((a, b) => (a ?? 0) + (b ?? 0), 0);

        const testRunsCount = metrics
          .flatMap((m) => m.tests?.map((t) => t.test_runs))
          .filter(Boolean).length;

        let alerts = 0;
        for (const m of metrics) {
          const tests = m.tests;
          if (!tests) continue;
          for (const t of tests) {
            alerts += t.test_runs?.count_open_alerts ?? 0;
          }
        }

        return [
          path,
          {
            metrics: metrics.length,
            tests: app_pit ? testRunsCount : testsCount,
            alerts,
            isCritical:
              metrics
                .map((m) =>
                  m.tests?.filter((t) => t.test_runs?.count_breaking_alerts)
                )
                .filter(Boolean)
                .flat().length > 0,
          } as TreeAlerts,
        ];
      })
  );
};
